<script>
  /**
   * Traer la historia desde una hoja de cálculo.
   *
   * Se copia de Excel y se pega. Nada se guarda hasta que veas el resumen y
   * lo apruebes: meter 200 filas a ciegas es la forma más rápida de ensuciar
   * una base de datos nueva.
   */
  import { partir, detectarColumnas, construir, resumir } from '../lib/importar.js';
  import { formatear } from '../lib/dinero.js';
  import { crearMovimiento, hoy, TIPO_MOVIMIENTO } from '../lib/db.js';
  import { guardarVarios } from '../lib/repositorio.js';

  let { abierta = $bindable(false), cuentas = [], alImportar = () => {} } = $props();

  let texto = $state('');
  let etapa = $state('pegar');       // pegar | revisar | listo
  let filas = $state([]);
  let mapa = $state({});
  let hayEncabezado = $state(true);
  let movimientos = $state([]);
  let descartadas = $state([]);
  let cuentaId = $state(null);
  let fechaPorDefecto = $state(hoy());
  let guardando = $state(false);
  let importadas = $state(0);

  $effect(() => {
    if (cuentas.length && !cuentaId) cuentaId = cuentas[0].id;
  });

  const CAMPOS = [
    ['monto', 'Monto', true],
    ['concepto', 'Concepto', false],
    ['fecha', 'Fecha', false],
    ['gmf', '4x1000', false],
    ['categoria', 'Categoría', false],
    ['saldo', 'Saldo (se ignora)', false],
  ];

  const resumen = $derived(movimientos.length ? resumir(movimientos) : null);

  function analizar() {
    filas = partir(texto);
    if (!filas.length) return;
    const d = detectarColumnas(filas);
    hayEncabezado = d.encabezado;
    mapa = d.mapa;
    recalcular();
    etapa = 'revisar';
  }

  function recalcular() {
    const r = construir(filas, mapa, {
      desdeFila: hayEncabezado ? 1 : 0,
      fechaPorDefecto,
    });
    movimientos = r.movimientos;
    descartadas = r.descartadas;
  }

  function asignar(campo, indice) {
    const v = indice === '' ? undefined : Number(indice);
    // Una columna no puede servir para dos cosas: si se reasigna, se libera
    // de donde estaba.
    const nuevo = { ...mapa };
    for (const k of Object.keys(nuevo)) if (nuevo[k] === v) delete nuevo[k];
    if (v === undefined) delete nuevo[campo];
    else nuevo[campo] = v;
    mapa = nuevo;
    recalcular();
  }

  async function confirmar() {
    if (guardando || !movimientos.length) return;
    guardando = true;
    try {
      const aGuardar = [];
      for (const m of movimientos) {
        const mov = crearMovimiento({
          tipo: m.tipo === 'ingreso' ? TIPO_MOVIMIENTO.INGRESO : TIPO_MOVIMIENTO.EGRESO,
          monto: m.monto,
          fecha: m.fecha || fechaPorDefecto,
          concepto: m.concepto,
          gmf: m.gmf,
          cuenta_origen: m.tipo === 'ingreso' ? null : cuentaId,
          cuenta_destino: m.tipo === 'ingreso' ? cuentaId : null,
          origen_dato: 'importado',
        });
        aGuardar.push(mov);

        // El 4x1000 que traía la hoja se guarda como movimiento hijo, igual
        // que cuando se registra a mano. Así el saldo cuadra sin excepciones.
        if (m.gmf > 0) {
          aGuardar.push(crearMovimiento({
            tipo: TIPO_MOVIMIENTO.EGRESO,
            monto: m.gmf,
            fecha: m.fecha || fechaPorDefecto,
            concepto: '4x1000',
            cuenta_origen: cuentaId,
            movimiento_padre_id: mov.id,
            origen_dato: 'importado',
          }));
        }
      }
      await guardarVarios('movimientos', aGuardar);
      importadas = movimientos.length;
      etapa = 'listo';
      alImportar();
    } finally {
      guardando = false;
    }
  }

  function cerrar() {
    texto = '';
    etapa = 'pegar';
    filas = [];
    movimientos = [];
    descartadas = [];
    abierta = false;
  }
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={cerrar} aria-label="Cerrar">←</button>
      <h1>Traer de Excel</h1>
    </header>

    <div class="contenido">
      {#if etapa === 'pegar'}
        <p class="explica">
          Abre tu hoja de cálculo, selecciona las filas <strong>con el
          encabezado</strong>, cópialas y pégalas aquí. La app reconoce las
          columnas sola.
        </p>

        <textarea
          bind:value={texto}
          placeholder="Pega aquí lo que copiaste de Excel…"
          rows="8"
          spellcheck="false"
        ></textarea>

        <button class="principal" disabled={!texto.trim()} onclick={analizar}>
          Analizar
        </button>

      {:else if etapa === 'revisar'}
        <section>
          <h2>¿Qué es cada columna?</h2>
          <p class="explica">
            Revisa que esté bien. Lo que la app adivinó aparece ya escogido.
          </p>
          {#each CAMPOS as [campo, etiqueta, obligatorio]}
            <label class="fila-mapa">
              <span class="etiqueta">
                {etiqueta}{#if obligatorio}<span class="req">*</span>{/if}
              </span>
              <select value={mapa[campo] ?? ''} onchange={(e) => asignar(campo, e.target.value)}>
                <option value="">— ninguna —</option>
                {#each filas[0] as celda, i}
                  <option value={i}>
                    {hayEncabezado ? celda : `Columna ${i + 1}`}
                  </option>
                {/each}
              </select>
            </label>
          {/each}

          <label class="casilla">
            <input type="checkbox" bind:checked={hayEncabezado} onchange={recalcular} />
            <span>La primera fila es el encabezado</span>
          </label>
        </section>

        <section>
          <h2>A qué cuenta van</h2>
          <select bind:value={cuentaId}>
            {#each cuentas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
          </select>
          {#if resumen?.sinFecha > 0}
            <label>
              <span class="etiqueta">
                Fecha para las {resumen.sinFecha} filas que no la traen
              </span>
              <input type="date" bind:value={fechaPorDefecto} max={hoy()} onchange={recalcular} />
            </label>
          {/if}
        </section>

        {#if resumen}
          <section class="resumen">
            <h2>Esto es lo que va a entrar</h2>
            <div class="numeros">
              <div><span class="grande cifra">{resumen.total}</span><span>movimientos</span></div>
              <div>
                <span class="grande cifra entra">{formatear(resumen.sumaIngresos)}</span>
                <span>{resumen.ingresos} entradas</span>
              </div>
              <div>
                <span class="grande cifra">{formatear(resumen.sumaEgresos)}</span>
                <span>{resumen.egresos} salidas</span>
              </div>
            </div>
            {#if resumen.gmfTotal > 0}
              <p class="dato">4x1000 que trae la hoja: <strong class="cifra">{formatear(resumen.gmfTotal)}</strong></p>
            {/if}
            {#if resumen.desde}
              <p class="dato">Del {resumen.desde} al {resumen.hasta}</p>
            {/if}
          </section>

          <!-- Las primeras filas, para que se vea que quedaron bien leídas -->
          <section>
            <h2>Las primeras cinco</h2>
            <div class="muestra">
              {#each movimientos.slice(0, 5) as m}
                <div class="fila-muestra">
                  <span class="concepto-m">{m.concepto || '(sin concepto)'}</span>
                  <span class="cifra" class:entra={m.tipo === 'ingreso'}>
                    {m.tipo === 'ingreso' ? '+' : '−'}{formatear(m.monto, { simbolo: false })}
                  </span>
                </div>
              {/each}
            </div>
          </section>
        {/if}

        {#if descartadas.length}
          <div class="alerta">
            <strong>{descartadas.length} filas no se pudieron leer</strong>
            <p>No tenían un monto reconocible. Se van a saltar.</p>
          </div>
        {/if}

        <div class="botones">
          <button class="secundario" onclick={() => (etapa = 'pegar')}>Atrás</button>
          <button class="principal" disabled={!movimientos.length || guardando} onclick={confirmar}>
            {guardando ? 'Importando…' : `Importar ${movimientos.length}`}
          </button>
        </div>

      {:else}
        <div class="listo">
          <span class="palomita" aria-hidden="true">✓</span>
          <h2 class="titulo-listo">Entraron {importadas} movimientos</h2>
          <p class="explica">
            Ya puedes ver tus gráficas y tus promedios con datos de verdad.
          </p>
          <button class="principal" onclick={cerrar}>Listo</button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold) }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-5);
  }

  section { display: flex; flex-direction: column; gap: var(--e-2) }

  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }

  .explica { font-size: var(--t-sm); color: var(--ink-2); line-height: var(--lh-loose) }

  textarea {
    padding: var(--e-3);
    font-size: var(--t-sm);
    font-family: ui-monospace, monospace;
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    resize: vertical;
    min-height: 160px;
  }

  label { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .req { color: var(--peligro) }

  .fila-mapa {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: var(--e-3);
  }
  .fila-mapa select { flex: 1; max-width: 60% }

  select,
  input[type='date'] {
    padding: var(--e-2) var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
  }

  .casilla { flex-direction: row; gap: var(--e-2); align-items: center }
  .casilla input { width: 18px; height: 18px }

  /* ── Resumen ── */
  .resumen {
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  .numeros { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--e-2) }
  .numeros div { display: flex; flex-direction: column; gap: 2px }
  .numeros span:last-child { font-size: var(--t-xs); color: var(--ink-3) }
  .grande { font-size: var(--t-lg); font-weight: var(--w-bold) }
  .entra { color: var(--ingreso) }
  .dato { font-size: var(--t-sm); color: var(--ink-2); margin-top: var(--e-2) }

  .muestra {
    border: 1px solid var(--border);
    border-radius: var(--r-md);
    overflow: hidden;
  }
  .fila-muestra {
    display: flex;
    justify-content: space-between;
    gap: var(--e-3);
    padding: var(--e-2) var(--e-3);
    font-size: var(--t-sm);
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
  }
  .fila-muestra:last-child { border-bottom: none }
  .concepto-m {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .alerta {
    font-size: var(--t-sm);
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--alerta-suave);
    color: var(--alerta);
    line-height: var(--lh-loose);
  }

  /* ── Listo ── */
  .listo { text-align: center; padding: var(--e-10) 0; display: flex; flex-direction: column; gap: var(--e-3) }
  .palomita {
    font-size: 48px;
    color: var(--ingreso);
    animation: brincar var(--d-pausada) var(--ease-resorte);
  }
  @keyframes brincar {
    from { transform: scale(0.3); opacity: 0 }
    to   { transform: scale(1); opacity: 1 }
  }
  .titulo-listo { font-size: var(--t-xl); font-weight: var(--w-bold); text-transform: none; letter-spacing: 0; color: var(--ink-1) }

  .botones { display: flex; gap: var(--e-2) }
  .secundario {
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal:disabled { opacity: 0.4 }
</style>
